import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useGame } from '../context/GameContext';
import Card from '../components/ui/Card';
import SafeIcon from '../components/common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { DateService } from '../services/dateService';
import { formatNumber } from '../utils/formatters';

const { FiDroplet, FiCloudRain } = FiIcons;

const HERO_IMAGE = 'https://cdn.midjourney.com/9697fcc3-16b9-4193-a351-57ccb8eb4fc3/0_2.webp';

export default function DailyWatering() {
  const { state, actions } = useGame();
  const [isCharging, setIsCharging] = useState(false);
  const [chargeLevel, setChargeLevel] = useState(0);
  const [showReward, setShowReward] = useState(false);
  const chargeIntervalRef = useRef();
  const prevUsedRef = useRef(state.dailyWater.used);

  const today     = DateService.getDayKey();
  const waterUsed = state.dailyWater.used && state.dailyWater.lastDay === today;

  const baseReward = 1000;
  const maxBoost   = 999000;

  const startCharging = () => {
    if (waterUsed) return;
    setIsCharging(true);
    setChargeLevel(0);
    chargeIntervalRef.current = setInterval(() => {
      setChargeLevel((p) => Math.min(p + 2, 100));
    }, 20);
  };

  const stopCharging = () => {
    setIsCharging(false);
    clearInterval(chargeIntervalRef.current);
    if (chargeLevel > 0 && !waterUsed) {
      actions.waterGarden(chargeLevel / 100);
    } else {
      setChargeLevel(0);
    }
  };

  useEffect(() => {
    if (state.dailyWater.used && !prevUsedRef.current) {
      setShowReward(true);
      const t = setTimeout(() => { setShowReward(false); setChargeLevel(0); }, 3000);
      return () => clearTimeout(t);
    }
  }, [state.dailyWater.used]);

  useEffect(() => { prevUsedRef.current = state.dailyWater.used; });
  useEffect(() => () => clearInterval(chargeIntervalRef.current), []);

  const minEst = Math.floor((baseReward + maxBoost * (chargeLevel / 100)) * 0.85);
  const maxEst = Math.floor((baseReward + maxBoost * (chargeLevel / 100)) * 1.15);

  return (
    <div className="space-y-6">
      <div className="relative h-48 lg:h-64 rounded-3xl overflow-hidden shadow-xl">
        <div className="absolute inset-0 bg-black/20 z-10" />
        <img src={HERO_IMAGE} alt="Watering" className="w-full h-full object-cover" />
        <div className="absolute inset-0 z-20 flex flex-col justify-end p-6 lg:p-8">
          <h1 className="text-3xl lg:text-4xl font-cormorant font-bold text-white mb-2">Daily Watering</h1>
          <p className="text-white/90 font-dm-sans max-w-lg">
            Nurture your garden with fresh water once a day. The deeper the soak, the greater the bloom.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="p-8 flex flex-col items-center justify-center text-center bg-gradient-to-b from-white to-skyblue/5">
          <div className="mb-8 w-full max-w-xs">
            <div className="flex justify-between text-xs font-bold text-charcoal/40 mb-2 uppercase tracking-wider">
              <span>Power</span>
              <span>{Math.round(chargeLevel)}%</span>
            </div>
            <div className="h-4 bg-gray-100 rounded-full overflow-hidden border border-gray-200">
              <motion.div
                className="h-full bg-gradient-to-r from-skyblue to-blue-500"
                initial={{ width: 0 }}
                animate={{ width: `${chargeLevel}%` }}
                transition={{ duration: 0.1 }}
              />
            </div>
          </div>

          <div className="relative mb-8">
            {waterUsed ? (
              <div className="w-48 h-48 rounded-full bg-gray-100 flex items-center justify-center border-4 border-dashed border-gray-300 text-gray-400">
                <div className="text-center">
                  <SafeIcon icon={FiCloudRain} className="w-12 h-12 mx-auto mb-2" />
                  <span className="font-bold text-sm">Come back tomorrow</span>
                </div>
              </div>
            ) : (
              <motion.button
                onMouseDown={startCharging} onMouseUp={stopCharging}
                onMouseLeave={stopCharging} onTouchStart={startCharging} onTouchEnd={stopCharging}
                whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}
                className={`w-48 h-48 rounded-full flex flex-col items-center justify-center shadow-[0_0_40px_rgba(109,174,219,0.3)] border-4 border-white transition-all duration-200
                  ${isCharging ? 'bg-gradient-to-br from-blue-400 to-skyblue text-white scale-105 shadow-[0_0_60px_rgba(109,174,219,0.6)]' : 'bg-gradient-to-br from-skyblue to-blue-300 text-white'}`}
              >
                <SafeIcon icon={FiDroplet} className={`w-16 h-16 mb-2 ${isCharging ? 'animate-bounce' : ''}`} />
                <span className="font-bold tracking-widest text-sm">
                  {isCharging ? 'CHARGING...' : 'HOLD TO WATER'}
                </span>
              </motion.button>
            )}
          </div>

          <div className="h-8 text-charcoal/60 font-dm-sans text-sm">
            {!waterUsed && chargeLevel > 0 && (
              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
                Estimated: <span className="font-bold text-skyblue">{formatNumber(minEst)} – {formatNumber(maxEst)}</span> seeds
              </motion.div>
            )}
            {waterUsed && 'Watering complete for today.'}
          </div>
        </Card>

        <div className="flex flex-col gap-6">
          <Card className="p-6 bg-skyblue/5 border-skyblue/20">
            <h3 className="text-lg font-cormorant font-bold text-charcoal mb-4 flex items-center gap-2">
              <SafeIcon icon={FiCloudRain} className="w-5 h-5 text-skyblue" />
              Hydration Mechanics
            </h3>
            <p className="text-sm text-charcoal/70 mb-4 leading-relaxed">
              Your garden requires daily hydration to maintain peak production.
            </p>
            <ul className="space-y-3">
              {[
                'Hold the button to build pressure.',
                'Release at 100% for maximum yield.',
                'Earn up to 1M seeds instantly.'
              ].map((tip, i) => (
                <li key={i} className="flex items-center gap-3 p-3 bg-white rounded-xl border border-skyblue/10 shadow-sm">
                  <div className="w-8 h-8 rounded-full bg-skyblue/20 flex items-center justify-center text-skyblue font-bold text-xs">{i + 1}</div>
                  <div className="text-sm text-charcoal/80">{tip}</div>
                </li>
              ))}
            </ul>
          </Card>
        </div>
      </div>

      <AnimatePresence>
        {showReward && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
          >
            <motion.div
              initial={{ scale: 0.8, y: 50 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.8, y: 50 }}
              className="bg-white rounded-3xl p-8 max-w-sm w-full text-center shadow-2xl relative overflow-hidden"
            >
              <div className="absolute inset-0 bg-gradient-to-b from-skyblue/20 to-transparent pointer-events-none" />
              <div className="w-24 h-24 bg-skyblue rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg shadow-skyblue/30 animate-bounce">
                <SafeIcon icon={FiDroplet} className="w-12 h-12 text-white" />
              </div>
              <h2 className="text-2xl font-cormorant font-bold text-charcoal mb-2">Garden Hydrated!</h2>
              <div className="text-4xl font-bold text-skyblue mb-2 font-dm-sans">
                +{formatNumber(state.lastWateringReward)}
              </div>
              <p className="text-charcoal/60 text-sm">Seeds added to your balance</p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}